/**
 * Subjects API.
 */

import { XLSX_MIME } from '@/constants/mime-types.constants';
import { apiClient } from './api-client';
import type { Subject, CreateSubjectDto, UpdateSubjectDto } from '@/types/subject.types';
import type { PaginatedResponse } from '@/types/data-table.types';

/** Append query params to a path when the search string is non-empty. */
function listUrl(path: string, searchParams: URLSearchParams): string {
  const q = searchParams.toString();
  return q ? `${path}?${q}` : path;
}

/** Trigger a browser download of an XLSX blob. */
function downloadXlsx(data: Blob, filename: string): void {
  const blob = new Blob([data], { type: XLSX_MIME });
  const url = globalThis.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  globalThis.URL.revokeObjectURL(url);
}

/** `GET /academic/subjects` — paginated subjects. */
export const getSubjects = async (params?: {
  page?: number;
  pageSize?: number;
  cycles?: string[];
  statuses?: string[];
  isCore?: boolean;
  search?: string;
  /** presentiel: pedagogical tutor order. Omitted: API default order. */
  displayOrder?: 'presentiel';
}): Promise<PaginatedResponse<Subject>> => {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.append('page', params.page.toString());
  if (params?.pageSize) searchParams.append('pageSize', params.pageSize.toString());
  params?.cycles?.forEach((c) => searchParams.append('cycles', c));
  params?.statuses?.forEach((s) => searchParams.append('statuses', s));
  if (params?.isCore !== undefined) searchParams.append('isCore', params.isCore.toString());
  if (params?.search) searchParams.append('search', params.search);
  if (params?.displayOrder) searchParams.append('displayOrder', params.displayOrder);
  const { data } = await apiClient.get<PaginatedResponse<Subject>>(
    listUrl('/academic/subjects', searchParams),
  );
  return data;
};

/** `GET /academic/subjects/:id` */
export const getSubject = async (id: string): Promise<Subject> => {
  const { data } = await apiClient.get(`/academic/subjects/${id}`);
  return data;
};

/** `POST /academic/subjects` */
export const createSubject = async (dto: CreateSubjectDto): Promise<Subject> => {
  const { data } = await apiClient.post('/academic/subjects', dto);
  return data;
};

/** `PATCH /academic/subjects/:id` */
export const updateSubject = async (id: string, dto: UpdateSubjectDto): Promise<Subject> => {
  const { data } = await apiClient.patch(`/academic/subjects/${id}`, dto);
  return data;
};

/** `DELETE /academic/subjects/:id` */
export const deleteSubject = async (id: string): Promise<void> => {
  await apiClient.delete(`/academic/subjects/${id}`);
};

/** `GET /academic/subjects/export` — download XLSX. */
export const exportSubjectsExcel = async (): Promise<void> => {
  const response = await apiClient.get('/academic/subjects/export', { responseType: 'blob' });
  downloadXlsx(response.data, `subjects-${new Date().toISOString().slice(0, 10)}.xlsx`);
};
